import { BeheerVestiging, VerlengingStatus, Vestiging, VestigingBeschikbareFaciliteiten, VestigingStatus } from "@/entities/vestiging";
import { AangepasteOpeningstijdenDto, AangevraagdeVestigingDto } from "@/shared/types";
import { stringToEnumKey } from "@/shared/utils";
import { parseISO } from "date-fns";
import { Openingstijden } from "./openingstijden";

export type AangevraagdeVestigingOpeningstijd = {
    dag: number; // Day of week (1=Monday, 7=Sunday)
    openVan: string; // HH:MM
    openTot: string; // HH:MM
    aangepasteOpenTot?: string; // HH:MM custom closing time
    aangepasteOpenTotStatus?: VerlengingStatus;
};

export const AangevraagdeVestigingOpeningstijd = {
    fromDto: (dto: AangepasteOpeningstijdenDto): AangevraagdeVestigingOpeningstijd => ({
        dag: dto.dag,
        openVan: dto.openVan,
        openTot: dto.openTot,
        aangepasteOpenTot: dto.aangepasteOpenTot,
        aangepasteOpenTotStatus: dto.aangepasteOpenTotStatus,
    }),

    toDto: (domain: AangevraagdeVestigingOpeningstijd): AangepasteOpeningstijdenDto => ({
        dag: domain.dag,
        openVan: domain.openVan,
        openTot: domain.openTot,
        aangepasteOpenTot: domain.aangepasteOpenTot,
        aangepasteOpenTotStatus: domain.aangepasteOpenTotStatus,
    }),
};

export type AangevraagdeVestiging = {
    id: number;
    vestigingId: number; // links to Vestiging.id
    dossiers: number[]; // can link to multiple dossiers
    openingstijden: AangevraagdeVestigingOpeningstijd[];
    aangevraagdOpDatumTijd: Date;
    laatstGewijzigdOpDatumTijd: Date;
    status: VestigingStatus;
    toelichtingKlantenservice?: string;
    stationCode: string;
    vestigingNaam: string;
    beschikbareFaciliteiten: VestigingBeschikbareFaciliteiten[];
    isNsRetailVestiging: boolean;
    telefoonnummer: string;
    opmerkingVestiging?: string;
};

export const AangevraagdeVestiging = {
    update: (av: AangevraagdeVestiging, update: Partial<AangevraagdeVestiging>): AangevraagdeVestiging => {
        return {
            ...av,
            ...update,
        };
    },
    groupByStation: (aangevraagdeVestiging: AangevraagdeVestiging[]) =>
        Object.groupBy(aangevraagdeVestiging, (av) => av.stationCode) as Record<string, AangevraagdeVestiging[]>,

    getOpeningstijden: (aangevraagdeVestiging: AangevraagdeVestiging): AangevraagdeVestigingOpeningstijd | undefined => {
        return aangevraagdeVestiging.openingstijden.find(
            (o: Openingstijden) => o.dag === BeheerVestiging.getDayOfTheWeek(aangevraagdeVestiging.aangevraagdOpDatumTijd)
        );
    },

    updateOpeningstijden: (
        vestiging: Vestiging,
        aangepasteOpenTot?: string | null,
        aangepasteOpenTotStatus?: VerlengingStatus | null
    ): AangevraagdeVestigingOpeningstijd[] => {
        if (!vestiging.aangevraagdeVestiging) {
            throw new Error("Fout bij het updaten van openingstijden: aangevraagdeVestiging is leeg");
        }
        const aanvraagdag = BeheerVestiging.getDayOfTheWeek(vestiging.aangevraagdeVestiging.aangevraagdOpDatumTijd);

        return vestiging.aangevraagdeVestiging.openingstijden.map((ot) =>
            ot.dag === aanvraagdag
                ? {
                      ...ot,
                      aangepasteOpenTot: aangepasteOpenTot ?? undefined,
                      aangepasteOpenTotStatus: aangepasteOpenTotStatus ?? undefined,
                  }
                : ot
        );
    },

    fromDto: (dto: AangevraagdeVestigingDto): AangevraagdeVestiging => ({
        id: dto.vestigingAanvraagId,
        vestigingId: dto.vestigingId,
        dossiers: dto.dossiers,
        openingstijden: dto.openingstijden.map(AangevraagdeVestigingOpeningstijd.fromDto),
        aangevraagdOpDatumTijd: parseISO(dto.aangevraagdOpDatumTijd),
        laatstGewijzigdOpDatumTijd: parseISO(dto.laatstGewijzigdOpDatumTijd),
        status: VestigingStatus[stringToEnumKey<typeof VestigingStatus>(dto.status)],
        toelichtingKlantenservice: dto.toelichtingKlantenservice,
        stationCode: dto.stationCode,
        vestigingNaam: dto.vestigingNaam,
        beschikbareFaciliteiten: dto.beschikbareFaciliteiten.map(
            (bf: string) => VestigingBeschikbareFaciliteiten[stringToEnumKey<typeof VestigingBeschikbareFaciliteiten>(bf)]
        ),
        isNsRetailVestiging: dto.isNsRetailVestiging,
        telefoonnummer: dto.telefoonnummer,
        opmerkingVestiging: dto.opmerkingVestiging,
    }),

    toDto: (domain: AangevraagdeVestiging): AangevraagdeVestigingDto => ({
        vestigingAanvraagId: domain.id,
        vestigingId: domain.vestigingId,
        dossiers: domain.dossiers,
        openingstijden: domain.openingstijden.map(AangevraagdeVestigingOpeningstijd.toDto),
        aangevraagdOpDatumTijd: domain.aangevraagdOpDatumTijd.toISOString(),
        laatstGewijzigdOpDatumTijd: domain.laatstGewijzigdOpDatumTijd.toISOString(),
        status: domain.status,
        toelichtingKlantenservice: domain.toelichtingKlantenservice,
        stationCode: domain.stationCode,
        vestigingNaam: domain.vestigingNaam,
        beschikbareFaciliteiten: domain.beschikbareFaciliteiten,
        isNsRetailVestiging: domain.isNsRetailVestiging,
        telefoonnummer: domain.telefoonnummer,
        opmerkingVestiging: domain.opmerkingVestiging,
    }),
};
